import React, { useMemo } from 'react';
import { useTheme } from '@mui/styles';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
  VictoryLabel,
  VictoryStack
} from 'victory';
import makeStyles from '@mui/styles/makeStyles';
import { Grid } from '@mui/material';
import { useIntl } from 'react-intl';
import { useMessage } from 'modules/intl';
import {
  getFormattedDuration,
  useFormattedDuration
} from 'modules/notes/common/components/Timer';
import { useChartStyles } from './hooks/useChartStyles';
import { resultDataSetProp } from './utils';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    height: '5vw'
  },
  dashedLine: {
    borderRight: '2px dashed grey',
    height: '60%'
  }
}));

type TeamProductivityDurationControlProp = {
  data: resultDataSetProp[][] | undefined;
  scheduledHours: number;
};

const TeamProductivityDurationControl: React.FC<TeamProductivityDurationControlProp> = ({
  data,
  scheduledHours
}: TeamProductivityDurationControlProp) => {
  const classes = useStyles();
  const theme = useTheme();
  const style = useChartStyles(theme);

  const scheduledHoursLabel = useFormattedDuration({
    needsMask: true,
    duration: scheduledHours ?? 0
  });

  const scheduledHoursMessage = useMessage(
    'insights.teamProductivity.chart.scheduledHours',
    { duration: scheduledHoursLabel }
  );
  const { formatMessage } = useIntl();
  const chartData = useMemo(() => {
    const victoryChart = data?.map((_data, index) => {
      return (
        <VictoryBar
          labels={({ datum }) =>
            `${getFormattedDuration({
              formatMessage,
              needsMask: true,
              duration: datum.value
            })}`
          }
          labelComponent={
            <VictoryLabel dx={-4} textAnchor="end" style={style.VictoryLabel} />
          }
          key={index}
          barWidth={style.VictoryBar.width}
          data={_data}
          style={{
            data: { fill: _data[0].color }
          }}
        />
      );
    });
    return victoryChart;
  }, [data, formatMessage, style.VictoryBar.width, style.VictoryLabel]);
  return (
    <Grid className={classes.container}>
      <VictoryChart
        height={style.VictoryChart.height}
        domainPadding={style.VictoryChart.domainPadding}
        padding={style.VictoryChart.padding}
        animate={{
          duration: 500,
          easing: 'bounce'
        }}
        containerComponent={<VictoryContainer responsive={true} />}
      >
        <VictoryAxis
          offsetX={0}
          orientation="right"
          style={style.VictoryAxis}
        />
        <VictoryLabel
          verticalAnchor="end"
          textAnchor="end"
          text={scheduledHoursMessage}
          x={445}
          y={-6}
          style={style.VictoryLabel}
        />
        <VictoryStack horizontal>{chartData}</VictoryStack>
      </VictoryChart>
      <div className={classes.dashedLine}></div>
    </Grid>
  );
};

export default React.memo(TeamProductivityDurationControl);
